import { z } from "zod";

/**
 * Wire contract for the Marketing Calendar (FOG-2165).
 *
 * These schemas are the source of truth the future connect-api endpoints
 * (event-schemas protos) will be written against; the MSW mocks and the page
 * both validate against them so swapping mocks for real endpoints is a
 * base-URL change, not a refactor. Shapes are modeled on the Blastro
 * campaigns sheets plus the Figma "Marketing Calendar V1"
 * prototype. Field names are snake_case to match connect-api conventions.
 */

/** Real QO send mediums. There is deliberately no "push" channel. */
export const marketingChannelSchema = z.enum(["email", "sms", "mms", "rcs"]);
export type MarketingChannel = z.infer<typeof marketingChannelSchema>;

/**
 * Campaign-level status shown on the calendar. Per-broadcast lifecycle
 * (approve/reject, sent) arrives with the approval work in a later slice.
 */
export const marketingCampaignStatusSchema = z.enum([
  "draft",
  "scheduled",
  "completed",
]);
export type MarketingCampaignStatus = z.infer<
  typeof marketingCampaignStatusSchema
>;

/** "agent" marks Blastro origin: same status colors, origin icon. */
export const marketingCampaignSourceSchema = z.enum(["agent", "manual"]);
export type MarketingCampaignSource = z.infer<
  typeof marketingCampaignSourceSchema
>;

/** Blastro brief size: small 1-2 sends, medium 3-4, big 5+ (A/B variants
 * sharing one broadcast count as one send). Explicit size is enforced;
 * omitted on propose lets Blastro upsize from Small (FOG-2586/CON-302). */
export const marketingCampaignSizeSchema = z.enum(["small", "medium", "big"]);
export type MarketingCampaignSize = z.infer<typeof marketingCampaignSizeSchema>;

const isoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");

/**
 * One send inside a campaign, read-only: the campaign stores just the
 * broadcast_id, and the display fields are resolved live from the audiences
 * service on every read (CON-186). Nothing broadcast-shaped is snapshotted.
 */
export const marketingBroadcastSchema = z.object({
  /** Real audiences-broadcast id. */
  broadcast_id: z.string(),
  name: z.string(),
  channel: marketingChannelSchema,
  /** ISO 8601 datetime, UTC. Null when send_instantly. */
  send_at: z.string().datetime().nullable(),
  send_instantly: z.boolean(),
});
export type MarketingBroadcast = z.infer<typeof marketingBroadcastSchema>;

/** Write shape of a campaign broadcast: the id alone. The backend refuses
 * ids audiences does not know and ignores any other fields, so none are sent. */
export const marketingBroadcastRefSchema = marketingBroadcastSchema.pick({
  broadcast_id: true,
});
export type MarketingBroadcastRef = z.infer<typeof marketingBroadcastRefSchema>;

/** Read-only id -> name projection of an audiences-service audience. */
export const audienceRefSchema = z.object({
  id: z.string(),
  name: z.string(),
});
export type AudienceRef = z.infer<typeof audienceRefSchema>;

/** Site-local "HH:MM" bound on a campaign date (CON-314). */
const campaignTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

/** dragon_tile matches a tile slug ("broadcast" = broadcasts); exchange
 * matches an optin id. Readers ignore unknown kinds, so keep it a string. */
export const offerOverrideExclusionSchema = z.object({
  kind: z.string(),
  match: z.string(),
  action: z.literal("OFFER_OVERRIDE_EXCLUSION_ACTION_EXCLUDE"),
});
export type OfferOverrideExclusion = z.infer<
  typeof offerOverrideExclusionSchema
>;

/** Swaps the campaign's offer into every Dragon Tile, broadcast and exchange
 * on the site for the window, minus exclusions (CON-312). Absent window
 * bounds inherit the campaign's dates + times in the site timezone. */
export const offerOverrideInputSchema = z.object({
  window_start: z.string().datetime().nullable().optional(),
  window_end: z.string().datetime().nullable().optional(),
  behavior: z.literal("OFFER_OVERRIDE_BEHAVIOR_SWAP_CODE"),
  exclusions: z.array(offerOverrideExclusionSchema).default([]),
});
export type OfferOverrideInput = z.infer<typeof offerOverrideInputSchema>;

export const offerOverrideSchema = offerOverrideInputSchema.extend({
  /** Output-only: the window in force after inheritance; null when the
   * site's timezone can't be loaded. Display this, don't recompute it. */
  effective_window_start: z.string().datetime().nullable().optional(),
  effective_window_end: z.string().datetime().nullable().optional(),
});
export type OfferOverride = z.infer<typeof offerOverrideSchema>;

/** A marketing event: a date span on the calendar owning its broadcasts. */
export const marketingCampaignSchema = z.object({
  id: z.string(),
  site_id: z.string(),
  name: z.string(),
  /** Free-form; the UI offers a suggested list plus custom entries. */
  event_type: z.string().nullable(),
  start_date: isoDateSchema,
  end_date: isoDateSchema,
  /** Null start = 00:00; null end = next local midnight after end_date. */
  start_time: campaignTimeSchema.nullable().optional(),
  end_time: campaignTimeSchema.nullable().optional(),
  status: marketingCampaignStatusSchema,
  source: marketingCampaignSourceSchema,
  /** Blastro-resolved size; null/absent on historical rows and until the
   * backend ships it (CON-302). Read-only on the FE: written via propose. */
  campaign_size: marketingCampaignSizeSchema.nullable().optional(),
  offer_slug: z.string().nullable(),
  promo_code: z.string().nullable(),
  promo_description: z.string().nullable(),
  /**
   * Announcement bar (FOG-2281): storage only — Blastro's updater does the
   * Shopify metaobject push with its own creds until FOG-2173. The URL is
   * stored-but-inert until the theme's metaobject gains a link field.
   */
  website_top_bar_content: z.string().nullable(),
  website_top_bar_url: z.string().nullable(),
  /** Null/absent = no override; inert without offer_slug. A PATCH carrying
   * the key replaces the whole block, null clears it. */
  offer_override: offerOverrideSchema.nullable().optional(),
  /** Audience seed for broadcasts added to the campaign (FOG-2419). Set on
   * create and PATCHable from the edit details step (hydrated round trip). */
  audience_ids: z.array(z.string()),
  /** Read-only display names for audience_ids, resolved by the backend. */
  audiences: z.array(audienceRefSchema),
  broadcasts: z.array(marketingBroadcastSchema),
  create_date: z.string().datetime(),
  modify_date: z.string().datetime(),
});
export type MarketingCampaign = z.infer<typeof marketingCampaignSchema>;

export const listMarketingCampaignsResponseSchema = z.object({
  campaigns: z.array(marketingCampaignSchema),
});
export type ListMarketingCampaignsResponse = z.infer<
  typeof listMarketingCampaignsResponseSchema
>;

/** One event type a site's marketers may pick for a campaign. A picker
 * source, not a constraint: campaign.event_type stays free text. */
export const marketingEventTypeSchema = z.object({
  name: z.string(),
});
export type MarketingEventType = z.infer<typeof marketingEventTypeSchema>;

export const listMarketingEventTypesResponseSchema = z.object({
  /** The built-in defaults unioned with the site's own custom types,
   * deduplicated case-insensitively (backend semantics). */
  event_types: z.array(marketingEventTypeSchema),
});
export type ListMarketingEventTypesResponse = z.infer<
  typeof listMarketingEventTypesResponseSchema
>;

// --- Payloads ---

export const createMarketingCampaignPayloadSchema = marketingCampaignSchema
  .omit({
    id: true,
    site_id: true,
    audiences: true,
    broadcasts: true,
    campaign_size: true,
    create_date: true,
    modify_date: true,
  })
  // The backend takes source as given (CON-188), defaulting absent to
  // "manual", so Blastro (and the dev seeder) can create as "agent".
  .partial({ source: true })
  // Broadcasts travel as id-only refs; display fields are read-only (CON-186).
  // The override's effective window is output-only, so it never travels.
  .extend({
    broadcasts: z.array(marketingBroadcastRefSchema),
    offer_override: offerOverrideInputSchema.strict().nullable().optional(),
  });
export type CreateMarketingCampaignPayload = z.infer<
  typeof createMarketingCampaignPayloadSchema
>;

/** Campaign PATCH. Broadcasts ARE updatable (stable-id refs, replacing the
 * array re-links, never re-ids). Status is NOT: the backend ignores it here —
 * transitions go through :schedule / :unschedule so broadcasts activate along. */
export const updateMarketingCampaignPayloadSchema =
  createMarketingCampaignPayloadSchema.omit({ status: true }).partial();
export type UpdateMarketingCampaignPayload = z.infer<
  typeof updateMarketingCampaignPayloadSchema
>;

/** Propose payload (FOG-2521): set fields constrain Blastro's planning,
 * omitted ones are generated. Wire shape of the campaigns:propose endpoint. */
export const proposeMarketingCampaignPayloadSchema = z.object({
  name: z.string().optional(),
  audience_ids: z.array(z.string()).optional(),
  event_type: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  website_top_bar_content: z.string().optional(),
  website_top_bar_url: z.string().optional(),
  /** Enforced when set; omitted lets Blastro upsize from Small (FOG-2586). */
  campaign_size: marketingCampaignSizeSchema.optional(),
  status: z.literal("draft").optional(),
});
export type ProposeMarketingCampaignPayload = z.infer<
  typeof proposeMarketingCampaignPayloadSchema
>;

/** 202 receipt: generation continues async with no completion callback or
 * status API; the campaign appears through the normal campaigns read. */
export const proposeMarketingCampaignResponseSchema = z.object({
  accepted: z.boolean(),
  execution_id: z.string(),
  status: z.string(),
});
export type ProposeMarketingCampaignResponse = z.infer<
  typeof proposeMarketingCampaignResponseSchema
>;
